/**
 * AdminMessaging.tsx
 * 
 * Admin messaging component for communicating with members
 * Supports email/SMS with templates and message history
 */

import { FC, useState } from 'react';
import { FiMail, FiMessageSquare, FiSend, FiClock, FiCheckCircle } from 'react-icons/fi';
import { ConnectionCase } from '../../services/plaidConnectionMonitorService';

interface AdminMessagingProps {
  selectedCase: ConnectionCase | null;
  onSendMessage: (message: MessagePayload) => Promise<void>;
  messageHistory?: MessageRecord[];
}

export interface MessagePayload {
  caseId: number;
  memberEmail: string;
  channel: 'email' | 'sms' | 'in-app';
  template?: string;
  subject?: string;
  body: string;
  metadata?: Record<string, any>;
}

export interface MessageRecord {
  id: string;
  caseId: number;
  channel: 'email' | 'sms' | 'in-app';
  subject?: string;
  body: string;
  sentAt: string;
  sentBy: string;
  status: 'sent' | 'delivered' | 'failed';
}

// Message templates based on common scenarios
const MESSAGE_TEMPLATES = {
  oauth_required: {
    subject: 'Update Required: Bank Connection Method',
    body: `Hi {member_name},

We noticed you tried to connect your {institution_name} account. This bank requires OAuth authentication, which means you'll need to use the "Connect with {institution_name}" button instead of entering your username and password directly.

To reconnect:
1. Click the "Connect Bank" button in your dashboard
2. Select "{institution_name}"
3. Click "Connect with {institution_name}"
4. You'll be redirected to your bank's secure login page

This provides better security and is required by {institution_name}.

Need help? Reply to this email or contact support.

Best regards,
VeraScore Support Team`
  },
  invalid_credentials: {
    subject: 'Action Required: Bank Connection Failed',
    body: `Hi {member_name},

Your recent attempt to connect {institution_name} failed due to invalid credentials. This usually happens when:

• Password was entered incorrectly
• Your bank password was recently changed
• Your account requires additional verification

Please try reconnecting with the correct credentials. If you recently changed your bank password, use the new one.

To reconnect:
1. Visit your VeraScore dashboard
2. Click "Reconnect" next to {institution_name}
3. Enter your current bank credentials

Need help? We're here to assist.

Best regards,
VeraScore Support Team`
  },
  institution_down: {
    subject: 'Temporary Issue: Bank Connection',
    body: `Hi {member_name},

We're currently experiencing a temporary issue connecting to {institution_name}. This is due to service disruptions on their end, not an issue with your account.

What this means:
• Your account is safe and secure
• No action is needed from you
• We're monitoring the situation

We'll automatically retry the connection once {institution_name} resolves the issue. You'll receive a notification once your account is successfully connected.

Thank you for your patience.

Best regards,
VeraScore Support Team`
  },
  mfa_required: {
    subject: 'Action Required: Multi-Factor Authentication',
    body: `Hi {member_name},

Your {institution_name} account requires multi-factor authentication (MFA) to complete the connection.

Please ensure:
• You have access to your 2FA device or app
• You can receive SMS codes (if applicable)
• Your authentication app is up to date

To complete the connection:
1. Visit your VeraScore dashboard
2. Click "Reconnect" next to {institution_name}
3. Complete the MFA verification when prompted

The verification code must be entered promptly as it expires quickly.

Need help? Contact us for assistance.

Best regards,
VeraScore Support Team`
  },
  general_reconnect: {
    subject: 'Action Required: Reconnect Your Bank Account',
    body: `Hi {member_name},

We need you to reconnect your {institution_name} account to keep your VeraScore up to date.

This takes less than a minute:
1. Visit your VeraScore dashboard
2. Click "Reconnect" next to {institution_name}
3. Follow the prompts to re-authenticate

Why reconnect?
• Keeps your financial data current
• Ensures accurate credit scoring
• Maintains account security

Questions? We're here to help.

Best regards,
VeraScore Support Team`
  }
};

const AdminMessaging: FC<AdminMessagingProps> = ({
  selectedCase,
  onSendMessage,
  messageHistory = []
}) => {
  const [channel, setChannel] = useState<'email' | 'sms' | 'in-app'>('email');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  if (!selectedCase) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-700 mb-2">Admin Messaging</h3>
        <p className="text-sm text-slate-500">Select a case to send messages to the member</p>
      </div>
    );
  }

  const handleTemplateSelect = (templateKey: string) => {
    setSelectedTemplate(templateKey);
    const template = MESSAGE_TEMPLATES[templateKey as keyof typeof MESSAGE_TEMPLATES];
    if (template) {
      setSubject(template.subject);

      // Smart member name detection
      // If member_name looks like an email prefix (no spaces, all lowercase), use first name format
      const memberName = selectedCase.member_name || 'Valued Member';
      const isEmailPrefix = memberName && !memberName.includes(' ') && memberName === memberName.toLowerCase();
      const displayName = isEmailPrefix
        ? memberName.charAt(0).toUpperCase() + memberName.slice(1) // Capitalize first letter
        : memberName;

      // Replace placeholders
      let filledBody = template.body
        .replace(/{member_name}/g, displayName)
        .replace(/{institution_name}/g, selectedCase.institution_name || 'your bank');
      setBody(filledBody);
    }
  };

  const handleSend = async () => {
    if (!body.trim() || !selectedCase.member_email) return;

    setSending(true);
    try {
      await onSendMessage({
        caseId: selectedCase.id,
        memberEmail: selectedCase.member_email,
        channel,
        template: selectedTemplate || undefined,
        subject: channel === 'email' ? subject : undefined,
        body: body.trim(),
        metadata: {
          institution_name: selectedCase.institution_name,
          classification: selectedCase.classification,
          error_code: selectedCase.plaid_link_sessions?.terminal_outcome
        }
      });

      // Clear form
      setSubject('');
      setBody('');
      setSelectedTemplate('');

      alert('Message sent successfully!');
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-700">
          📧 Contact Member
        </h3>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          {showHistory ? 'Hide' : 'View'} Message History ({messageHistory.length})
        </button>
      </div>

      {showHistory && (
        <div className="mb-4 p-4 bg-slate-50 rounded-lg max-h-60 overflow-y-auto">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Message History</h4>
          {messageHistory.length === 0 ? (
            <p className="text-xs text-slate-500">No messages sent yet</p>
          ) : (
            <div className="space-y-2">
              {messageHistory.map((msg) => (
                <div key={msg.id} className="bg-white p-3 rounded border border-slate-200">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {msg.channel === 'email' ? <FiMail size={14} /> : <FiMessageSquare size={14} />}
                      <span className="text-xs font-medium text-slate-700">{msg.subject || 'Message'}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      msg.status === 'delivered' ? 'bg-green-100 text-green-700' :
                      msg.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {msg.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mb-1 line-clamp-2">{msg.body}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <FiClock size={10} />
                    {new Date(msg.sentAt).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-4">
        <p className="text-sm text-slate-600 mb-3">
          Member: <strong>{(() => {
            const name = selectedCase.member_name || 'Unknown';
            // If it looks like an email prefix (no spaces, all lowercase), capitalize it
            if (name && !name.includes(' ') && name === name.toLowerCase() && name !== 'Unknown') {
              return name.charAt(0).toUpperCase() + name.slice(1);
            }
            return name;
          })()}</strong>
          {selectedCase.member_email && (
            <span className="ml-2 text-blue-600">({selectedCase.member_email})</span>
          )}
        </p>

        {/* Channel Selection */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-slate-600 mb-2">Channel</label>
          <div className="flex gap-2">
            <button
              onClick={() => setChannel('email')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                channel === 'email'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <FiMail size={16} />
              Email
            </button>
            <button
              onClick={() => setChannel('sms')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                channel === 'sms'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <FiMessageSquare size={16} />
              SMS
            </button>
            <button
              onClick={() => setChannel('in-app')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                channel === 'in-app'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              disabled
              title="Coming soon"
            >
              In-App (Soon)
            </button>
          </div>
        </div>

        {/* Template Selection */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-slate-600 mb-2">Quick Templates</label>
          <select
            value={selectedTemplate}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Select a template --</option>
            <option value="oauth_required">OAuth Required</option>
            <option value="invalid_credentials">Invalid Credentials</option>
            <option value="institution_down">Institution Down</option>
            <option value="mfa_required">MFA Required</option>
            <option value="general_reconnect">General Reconnect</option>
          </select>
        </div>

        {/* Subject (Email only) */}
        {channel === 'email' && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-600 mb-2">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {/* Message Body */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-slate-600 mb-2">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={channel === 'sms' ? 'Enter SMS message (160 char limit)' : 'Enter your message'}
            rows={channel === 'sms' ? 3 : 8}
            maxLength={channel === 'sms' ? 160 : undefined}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          {channel === 'sms' && (
            <p className="text-xs text-slate-500 mt-1">{body.length}/160 characters</p>
          )}
        </div>

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={sending || !body.trim() || (channel === 'email' && !subject.trim())}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {sending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Sending...
            </>
          ) : (
            <>
              <FiSend size={16} />
              Send {channel === 'email' ? 'Email' : channel === 'sms' ? 'SMS' : 'Message'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AdminMessaging;
